import { generateError } from "../api/cardApiError";
import { CardState, ScanState } from "../api/cardApiModel";
import { EligibilityApiError } from "../api/eligibilityApiError";
import { GlobalLogger } from "./debugLogger";
import { _handleError } from "./scanViewErrorHandlerHelper";
import { generateHash } from "../utils/generateHash";
import {
  getFromLocalStorage,
  setInLocalStorage,
} from "./helpers/localStorageHelper";

//---------------------------------------
// Websocket message handling
//---------------------------------------

export const webSocketHandler = (
  cardScanApi: any,
  cardIdRef: any,
  onError: any,
  stateMachineState: any,
  stateMachineSend: any,
  backSideSupport: boolean | undefined,
  eligibilitySupport: boolean,
  onEligibilityError: ((error: EligibilityApiError) => void) | undefined
) => {
  if (cardScanApi?.current?.socket) {
    const lastEvents = getFromLocalStorage<string[]>("lastEvents") || [];
    const lastHashes = getFromLocalStorage<string[]>("lastHashes") || [];

    cardScanApi.current.socket.onopen = () => {
      GlobalLogger.debug("Socket Successfully Opened");

      //register current card on socket creation -- check card_id length to avoid race condition.
      if (cardIdRef.current && cardIdRef.current.length > 0) {
        const payload = JSON.stringify({
          card_id: cardIdRef.current,
          action: "register",
          source: "onopen",
        });
        cardScanApi?.current?.socket.send(payload);
      }
    };

    cardScanApi.current.socket.onclose = (event: CloseEvent) => {
      GlobalLogger.debug(`websocket closed ${event.code} ${event.reason}`);
      if (event.code == 1005 && event.wasClean) {
        GlobalLogger.debug("websocket closed cleanly");
      } else if (event.code == 1000) {
        GlobalLogger.debug("websocket closed cleanly");
      } else {
        const error = generateError(
          "The connection was closed abnormally",
          "WSError",
          event.code
        );
        _handleError(error, stateMachineSend, onError, stateMachineState);
      }
    };

    cardScanApi.current.socket.onerror = (event: Event) => {
      let error;
      if (stateMachineState.context.httpCode == 403) {
        error = generateError(
          stateMachineState.context.httpMessage,
          "WSError",
          403
        );
      } else {
        error = generateError("Unknown error from websocket", "WSError", 642);
      }
      GlobalLogger.debug(`websocket error ${error.message}`);
      _handleError(error, stateMachineSend, onError, stateMachineState);
    };

    cardScanApi.current.socket.onmessage = async (event: MessageEvent) => {
      //early exit for keepalive
      if (event.data == "pong") {
        return;
      }

      let _type = "";
      try {
        const { type } = JSON.parse(event.data);
        _type = type;
      } catch (error) {
        GlobalLogger.error("Unable to parse event received from websocket");
        GlobalLogger.debug(`${event}`);
      }

      if (_type == "card") {
        const {
          state,
          details,
          error,
          created_at,
          card_id,
          session_id,
          event_id,
        } = JSON.parse(event.data);

        const cardState = state as CardState;
        const isCardIdValid = card_id && cardIdRef.current !== card_id;
        const isStateMachineInRetryOrError =
          stateMachineState.value === "retry" ||
          stateMachineState.value === "error";
        const isSessionIdMatching =
          session_id === stateMachineState.context.session_id;
        const isCardPending = cardState === "pending";

        if (
          isCardIdValid &&
          isCardPending &&
          isStateMachineInRetryOrError &&
          isSessionIdMatching
        ) {
          GlobalLogger.debug(
            `Card - Overwrite cardIdRef: ${cardIdRef.current} with ${card_id}`
          );
          cardIdRef.current = card_id;
        }
        const hash = await generateHash(`${state}-${card_id}-${created_at}`);
        const sameCardId = cardIdRef.current === card_id;
        const newEvent = !lastEvents.includes(event_id);
        const newHash = !lastHashes.includes(hash);

        // prevent to send a message for a different cardId or not a new event or hash
        if (!sameCardId || !newEvent || !newHash) {
          return;
        }

        // stored the last eventId and hash
        lastEvents.push(event_id);
        lastHashes.push(hash);

        GlobalLogger.debug(
          `Card Event Received: cardId: ${card_id} state:${cardState} - ui:${stateMachineState.value}`
        );

        GlobalLogger.debug(
          `Card Event Payload: ${JSON.stringify(event.data)}`,
          "everything"
        );

        stateMachineSend(`card.${cardState}`, {
          card_id,
          backSideSupport,
          eligibilitySupport,
          session_id,
        });
      } else if (_type == "scan") {
        const { state, error, scan_id, card_id, event_id, created_at } =
          JSON.parse(event.data);
        const scanState = state as ScanState;

        // If we receive a skipped card error,
        // and we are in an error state or cameraError state
        // and the card is in completed state
        // we need to transition to completed state
        // This is a workaround for when we use web-to-mobile handoff
        // and the user completes the scan on mobile
        // the desktop widget will receive the completed message
        if (
          error &&
          error.error === "SkippedCard" &&
          error.message ===
            "Skipping scan for card received in completed state" &&
          ["error", "cameraError"].some(stateMachineState.matches)
        ) {
          stateMachineSend(`card.completed`);
        }
        const hash = await generateHash(`${state}-${scan_id}-${created_at}`);
        const sameCardId = cardIdRef.current === card_id;
        const newEvent = !lastEvents.includes(event_id);
        const newHash = !lastHashes.includes(hash);

        // prevent to send a message for a different cardId or not a new event or hash
        if (!sameCardId || !newEvent || !newHash) {
          return;
        }

        lastEvents.push(event_id);
        lastHashes.push(hash);
        GlobalLogger.debug(
          `Scan Event Received: scanId: ${scan_id} scan_state:${scanState} cardId: ${card_id} - ui:${stateMachineState.value}`
        );
        GlobalLogger.debug(
          `Scan Event Payload: ${JSON.stringify(event.data)}`,
          "everything"
        );
        stateMachineSend(`scan.${scanState}`, { card_id, scan_id });
      } else if (_type == "eligibility") {
        const { state, error, card_id, event_id, created_at, eligibility_id } =
          JSON.parse(event.data);

        const hash = await generateHash(
          `${state}-${eligibility_id}-${created_at}`
        );
        const sameCardId = cardIdRef.current === card_id;
        const newEvent = !lastEvents.includes(event_id);
        const newHash = !lastHashes.includes(hash);

        // prevent to send a message for a different cardId or not a new event or hash
        if (!sameCardId || !newEvent || !newHash) {
          return;
        }

        lastEvents.push(event_id);
        lastHashes.push(hash);

        if (error && onEligibilityError) onEligibilityError(error);

        stateMachineSend(`eligibility.${state}`);
      } else {
        GlobalLogger.log("Unknown event received from websocket");
        GlobalLogger.debug(`Unknown Payload: ${JSON.stringify(event.data)}`);
      }

      setInLocalStorage("lastEvents", lastEvents);
      setInLocalStorage("lastHashes", lastHashes);
    };
  } else {
    const error = generateError(
      "No websocket found - critical failure",
      "WSError",
      640
    );
    _handleError(error, stateMachineSend, onError, stateMachineState);
  }
};
